import React, {
  useContext,
  useState,
  useMemo,
  useCallback,
  useEffect,
} from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  FaShoppingCart,
  FaCheckCircle,
  FaBoxOpen,
  FaClock,
  FaShieldAlt,
} from "react-icons/fa";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { base_url } from "../utils/axiosConfig";
import { CurrencyContext } from "../features/context/CurrencyContext";
import { findCategoryHierarchy } from "../utils/findCategoryHierarchy";

const hasValue = (val) => {
  if (val === null || val === undefined) return false;
  if (typeof val === "string" && val.trim() === "") return false;
  if (Array.isArray(val) && val.length === 0) return false;
  return true;
};

const cleanList = (items) => {
  if (!Array.isArray(items)) return [];
  return items.filter(
    (item) => item !== null && item !== undefined && String(item).trim() !== "",
  );
};

const ReadMoreList = ({ items, field, label, showMore, toggleShowMore }) => {
  const cleaned = cleanList(items);
  if (cleaned.length === 0) return null;

  const maxItems = 4;
  const shouldShowReadMore = cleaned.length > maxItems;
  const displayedItems = showMore[field] ? cleaned : cleaned.slice(0, maxItems);

  return (
    <div className="mb-4">
      <h2 className="text-sm font-semibold text-gray-900 mb-1.5">{label}</h2>
      <ul className="text-xs text-gray-600 list-disc pl-4 space-y-1">
        {displayedItems.map((item, index) => (
          <li key={index} className="leading-snug">
            {item}
          </li>
        ))}
      </ul>
      {shouldShowReadMore && (
        <button
          onClick={() => toggleShowMore(field)}
          className="text-[#35A93E] font-medium hover:underline mt-1 text-xs"
        >
          {showMore[field] ? "Show Less ▲" : "Read More ▼"}
        </button>
      )}
    </div>
  );
};

const VariationOptions = ({
  variations,
  selectedVariation,
  onSelectVariation,
}) => {
  if (!variations?.length) return null;

  return (
    <div className="mb-4">
      <h3 className="font-semibold text-gray-900 mb-2 text-xs">
        Select Option
      </h3>
      <div className="flex flex-wrap gap-2">
        {variations.map((variation) => {
          const parts = [];
          if (hasValue(variation.size)) parts.push(`Size: ${variation.size}`);
          if (hasValue(variation.color))
            parts.push(`Color: ${variation.color}`);
          const label = parts.length > 0 ? parts.join(" • ") : "Standard";

          return (
            <button
              key={variation._id}
              onClick={() => onSelectVariation(variation)}
              className={`px-3 py-1.5 text-xs font-medium rounded-lg border transition-all ${
                selectedVariation?._id === variation._id
                  ? "border-[#35A93E] bg-[#35A93E]/10 text-[#2d8f35]"
                  : "border-gray-200 text-gray-700 hover:border-gray-300 hover:bg-gray-50"
              }`}
            >
              {label}
            </button>
          );
        })}
      </div>
    </div>
  );
};

const DetailRow = ({ label, value }) => {
  if (!hasValue(value)) return null;
  return (
    <div className="flex justify-between py-1.5 border-b border-gray-100 last:border-0 text-xs">
      <span className="text-gray-500">{label}</span>
      <span className="font-medium text-gray-900 text-right max-w-[60%]">
        {value}
      </span>
    </div>
  );
};

const ProductDetails = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const { product, category, subCategory, subSubCategory, subSubSubCategory } =
    location.state || {};

  const { convertPrice, getCurrencySymbol } = useContext(CurrencyContext);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setLoading(true);
        const response = await fetch(
          `${base_url}product/getCategory?page=1&limit=1000&sortingColumn=createdAt&sortingOrder=asc`,
        );
        if (!response.ok)
          throw new Error(`HTTP error! status: ${response.status}`);
        const data = await response.json();
        setCategories(data?.data || []);
      } catch (err) {
        console.error(err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchCategories();
  }, []);

  const hierarchy = findCategoryHierarchy(categories, {}, id);

  const displayProduct =
    product ||
    categories
      ?.flatMap((cat) =>
        (cat.subCategories || []).flatMap((subCat) => [
          ...(subCat.products || []),
          ...(subCat.subSubCategories || []).flatMap((subSubCat) => [
            ...(subSubCat.products || []),
            ...(subSubCat.subSubSubCategories || []).flatMap(
              (subSubSubCat) => subSubSubCat.products || [],
            ),
          ]),
        ]),
      )
      .find((p) => p._id === id);

  useEffect(() => {
    if (displayProduct && !product && !loading) {
      navigate(`/product-details/${id}`, {
        state: {
          product: displayProduct,
          category: hierarchy.category,
          subCategory: hierarchy.subCategory,
          subSubCategory: hierarchy.subSubCategory,
          subSubSubCategory: hierarchy.subSubSubCategory,
        },
        replace: true,
      });
    }
  }, [displayProduct, product, loading, navigate, id, hierarchy]);

  useEffect(() => {
    if (!displayProduct && !loading) {
      navigate("/", { replace: true });
    }
  }, [displayProduct, loading, navigate]);

  const isDigital = displayProduct?.productType === "digital";
  const isPhysical = !isDigital;

  const [selectedVariation, setSelectedVariation] = useState(
    displayProduct?.variations?.[0] || null,
  );
  const [selectedImage, setSelectedImage] = useState(
    displayProduct?.variations?.[0]?.images?.[0] ||
      displayProduct?.images?.[0] ||
      null,
  );

  const [showMore, setShowMore] = useState({
    benefits: false,
    features: false,
    instructionsForUse: false,
    batteryInfo: false,
    operatingConditions: false,
    packageContents: false,
    precautions: false,
    powerRequirements: false,
  });

  const [token, setToken] = useState(() =>
    localStorage.getItem("user_website")
      ? JSON.parse(localStorage.getItem("user_website"))
      : null,
  );

  useEffect(() => {
    const handleStorageChange = () => {
      const user = localStorage.getItem("user_website");
      setToken(user ? JSON.parse(user) : null);
    };
    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, []);

  const currentPrice = useMemo(() => {
    if (isPhysical && selectedVariation?.price?.amount != null) {
      return selectedVariation.price.amount;
    }
    if (displayProduct?.price?.amount != null) {
      return displayProduct.price.amount;
    }
    if (displayProduct?.variations?.[0]?.price?.amount != null) {
      return displayProduct.variations[0].price.amount;
    }
    return null;
  }, [isPhysical, selectedVariation, displayProduct]);

  const imageList = useMemo(() => {
    if (isPhysical && selectedVariation?.images?.length) {
      return selectedVariation.images.filter((img) => hasValue(img));
    }
    return (displayProduct?.images || []).filter((img) => hasValue(img));
  }, [isPhysical, selectedVariation, displayProduct]);

  useEffect(() => {
    if (imageList.length > 0) {
      setSelectedImage(imageList[0]);
    } else {
      setSelectedImage(null);
    }
  }, [imageList]);

  const mappedProduct = useMemo(
    () => ({
      _id: displayProduct?._id,
      productName: displayProduct?.productName,
      briefDescription: displayProduct?.briefDescription,
      benefits: cleanList(displayProduct?.benefits),
      features: cleanList(displayProduct?.features),
      instructionsForUse: cleanList(displayProduct?.instructionsForUse),
      batteryInfo: cleanList(displayProduct?.batteryInfo),
      operatingConditions: cleanList(displayProduct?.operatingConditions),
      packageContents: cleanList(displayProduct?.packageContents),
      precautions: cleanList(displayProduct?.precautions),
      powerRequirements: cleanList(displayProduct?.powerRequirements),
      packageType: displayProduct?.packageType,
      delivery: displayProduct?.delivery,
      rfidNfcCard: displayProduct?.rfidNfcCard,
      activation: displayProduct?.activation,
      material: displayProduct?.material,
      brandName: displayProduct?.brandName,
      manufacturerName: displayProduct?.manufacturerName,
      hsnCode: displayProduct?.hsnCode,
      duration_days: displayProduct?.duration_days,
      productType: displayProduct?.productType || "physical",
      weight: selectedVariation?.weight,
      dimensions: selectedVariation?.dimensions,
      size: selectedVariation?.size,
      color: selectedVariation?.color,
      quantity: displayProduct?.quantity,
    }),
    [displayProduct, selectedVariation],
  );

  const dimensionsLabel = useMemo(() => {
    const d = mappedProduct.dimensions;
    if (!d) return null;
    const parts = [];
    if (hasValue(d.length)) parts.push(`L ${d.length}`);
    if (hasValue(d.width)) parts.push(`W ${d.width}`);
    if (hasValue(d.height)) parts.push(`H ${d.height}`);
    return parts.length > 0 ? parts.join(" × ") : null;
  }, [mappedProduct.dimensions]);

  const handleVariationChange = useCallback((variation) => {
    setSelectedVariation(variation);
    const imgs = (variation?.images || []).filter((img) => hasValue(img));
    if (imgs.length > 0) {
      setSelectedImage(imgs[0]);
    }
  }, []);

  const toggleShowMore = useCallback((field) => {
    setShowMore((prev) => ({ ...prev, [field]: !prev[field] }));
  }, []);

  const addToCart = useCallback(async () => {
    try {
      if (!token?.token) {
        toast.error("Please log in to add items to cart");
        return;
      }

      const isWalletUser = token?.userType === "member";
      if (
        isWalletUser &&
        [
          "686bc4919224b59b776bf19e",
          "686bc5f09224b59b776bf1a8",
          "686d10b27dcf8686e68ebe09",
          "6877a12b7401d33df403bad7",
        ].includes(displayProduct?._id)
      ) {
        toast.error(
          "Members who already have a package at signup cannot purchase another package.",
        );
        return;
      }

      const cartUrl = isWalletUser
        ? `${base_url}product/addtocartMember`
        : `${base_url}product/addtoCart`;

      const cartBody = {
        productSource: "company",
        productId: displayProduct?._id,
        subCategoryId: subCategory || hierarchy.subCategory?._id,
        categoryId: category || hierarchy.category?._id,
        subSubCategoryId: subSubCategory || hierarchy.subSubCategory?._id,
        subSubSubCategoryId:
          subSubSubCategory || hierarchy.subSubSubCategory?._id,
        variationId: selectedVariation?._id,
        quantity: 1,
      };

      const response = await fetch(cartUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token.token}`,
        },
        body: JSON.stringify(cartBody),
      });

      const data = await response.json();
      if (!response.ok)
        throw new Error(data?.message || "Failed to add to cart");

      toast.success(data?.message || "Added to cart!");
      navigate("/cart");
    } catch (error) {
      toast.error(error.message || "Failed to add to cart");
    }
  }, [
    token,
    displayProduct,
    category,
    subCategory,
    subSubCategory,
    subSubSubCategory,
    selectedVariation,
    hierarchy,
    navigate,
  ]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#fafafa]">
        <div className="flex flex-col items-center gap-2">
          <div className="h-8 w-8 border-[3px] border-[#35A93E] border-t-transparent rounded-full animate-spin" />
          <p className="text-xs text-gray-500">Loading product…</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center text-red-500 text-xs">
        Error: {error}
      </div>
    );
  }

  if (!displayProduct) return null;

  return (
    <div className="min-h-screen bg-[#fafafa] pb-10">
      <div className="max-w-6xl mx-auto px-3 sm:px-5 lg:px-6 py-5">
        {/* Type badge */}
        <div className="mb-3">
          <span
            className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1 rounded-full ${
              isDigital
                ? "bg-blue-50 text-blue-700"
                : "bg-green-50 text-[#2d8f35]"
            }`}
          >
            {isDigital ? (
              <>
                <FaShieldAlt className="w-2.5 h-2.5" /> Digital Product
              </>
            ) : (
              <>
                <FaBoxOpen className="w-2.5 h-2.5" /> Physical Product
              </>
            )}
          </span>
        </div>

        <div className="grid lg:grid-cols-2 gap-5 lg:gap-8">
          {/* ========== LEFT: Image ========== */}
          <div className="lg:sticky lg:top-4 self-start space-y-3">
            <div
              className={`rounded-xl overflow-hidden border ${
                isDigital
                  ? "bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-100"
                  : "bg-white border-gray-100"
              } shadow-sm`}
            >
              <div className="aspect-square flex items-center justify-center p-4 sm:p-6">
                {selectedImage ? (
                  <img
                    src={selectedImage}
                    alt={mappedProduct.productName || "Product"}
                    className="w-full h-full object-contain max-h-[360px] transition-transform duration-300 hover:scale-105"
                    loading="lazy"
                  />
                ) : (
                  <div className="text-gray-300 text-xs">No image</div>
                )}
              </div>
            </div>

            {/* Thumbnails */}
            {imageList.length > 1 && (
              <div className="flex gap-2 overflow-x-auto pb-0.5">
                {imageList.map((img, index) => (
                  <button
                    key={index}
                    onClick={() => setSelectedImage(img)}
                    className={`w-14 h-14 flex-shrink-0 rounded-lg overflow-hidden border-2 transition-all ${
                      selectedImage === img
                        ? "border-[#35A93E] shadow-sm"
                        : "border-gray-200 hover:border-gray-300"
                    }`}
                  >
                    <img
                      src={img}
                      alt=""
                      className="w-full h-full object-contain bg-white p-0.5"
                    />
                  </button>
                ))}
              </div>
            )}

            {/* Specs card */}
            <div className="bg-white rounded-xl border border-gray-100 p-3.5 shadow-sm">
              <h3 className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-2">
                {isDigital ? "Package Details" : "Product Specs"}
              </h3>

              {isDigital ? (
                <div>
                  <DetailRow label="Brand" value={mappedProduct.brandName} />
                  <DetailRow
                    label="Manufacturer"
                    value={mappedProduct.manufacturerName}
                  />
                  <DetailRow label="HSN Code" value={mappedProduct.hsnCode} />
                  <DetailRow
                    label="Package Type"
                    value={mappedProduct.packageType}
                  />
                  <DetailRow label="Delivery" value={mappedProduct.delivery} />
                  <DetailRow
                    label="RFID / NFC"
                    value={mappedProduct.rfidNfcCard}
                  />
                  <DetailRow
                    label="Activation"
                    value={mappedProduct.activation}
                  />
                  {hasValue(mappedProduct.duration_days) && (
                    <DetailRow
                      label="Duration"
                      value={`${mappedProduct.duration_days} day${
                        mappedProduct.duration_days > 1 ? "s" : ""
                      }`}
                    />
                  )}
                </div>
              ) : (
                <div>
                  <DetailRow label="Brand" value={mappedProduct.brandName} />
                  <DetailRow
                    label="Manufacturer"
                    value={mappedProduct.manufacturerName}
                  />
                  <DetailRow label="HSN Code" value={mappedProduct.hsnCode} />
                  <DetailRow label="Material" value={mappedProduct.material} />
                  <DetailRow label="Size" value={mappedProduct.size} />
                  <DetailRow label="Color" value={mappedProduct.color} />
                  {hasValue(mappedProduct.weight) && (
                    <DetailRow
                      label="Weight"
                      value={`${mappedProduct.weight} kg`}
                    />
                  )}
                  <DetailRow label="Dimensions" value={dimensionsLabel} />
                  <DetailRow
                    label="Package Type"
                    value={mappedProduct.packageType}
                  />
                  <DetailRow
                    label="Delivery"
                    value={mappedProduct.delivery || "5–7 business days"}
                  />
                </div>
              )}
            </div>
          </div>

          {/* ========== RIGHT: Info ========== */}
          <div className="space-y-4">
            <div>
              {hasValue(mappedProduct.productName) && (
                <h1 className="text-base sm:text-lg font-semibold text-gray-900 leading-snug">
                  {mappedProduct.productName}
                </h1>
              )}
              {hasValue(mappedProduct.briefDescription) && (
                <p className="mt-1.5 text-xs text-gray-600 leading-snug whitespace-pre-line">
                  {mappedProduct.briefDescription}
                </p>
              )}
            </div>

            {/* Price + CTA */}
            <div
              className={`rounded-xl p-3.5 flex flex-wrap items-center justify-between gap-3 ${
                isDigital
                  ? "bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-100"
                  : "bg-white border border-gray-100 shadow-sm"
              }`}
            >
              <div>
                <p className="text-[10px] text-gray-500 mb-0.5">
                  {isDigital ? "Package Price" : "Price"}
                </p>
                <p className="text-lg font-bold text-gray-900">
                  {currentPrice != null ? (
                    <>
                      {getCurrencySymbol()}
                      {convertPrice(currentPrice)}
                    </>
                  ) : (
                    "—"
                  )}
                </p>
                {isDigital && hasValue(mappedProduct.duration_days) && (
                  <p className="text-[10px] text-gray-500 mt-0.5 flex items-center gap-1">
                    <FaClock className="w-2.5 h-2.5" />
                    Valid for {mappedProduct.duration_days} day
                    {mappedProduct.duration_days > 1 ? "s" : ""}
                  </p>
                )}
              </div>

              <button
                onClick={addToCart}
                className={`font-semibold text-xs px-4 py-2.5 rounded-lg flex items-center gap-2 transition shadow-sm ${
                  isDigital
                    ? "bg-blue-600 hover:bg-blue-700 text-white"
                    : "bg-[#35A93E] hover:bg-[#2e8b35] text-white"
                }`}
              >
                <FaShoppingCart className="w-3.5 h-3.5" />
                {isDigital ? "Get Package" : "Add to Cart"}
              </button>
            </div>

            {/* Variations */}
            {isPhysical && (
              <VariationOptions
                variations={displayProduct?.variations}
                selectedVariation={selectedVariation}
                onSelectVariation={handleVariationChange}
              />
            )}

            {/* Digital chips */}
            {isDigital && (
              <div className="flex flex-wrap gap-1.5">
                {hasValue(mappedProduct.rfidNfcCard) && (
                  <span className="inline-flex items-center gap-1 text-[10px] font-medium bg-white border border-blue-100 text-blue-700 px-2 py-1 rounded-full">
                    <FaCheckCircle className="w-2.5 h-2.5" />
                    {mappedProduct.rfidNfcCard}
                  </span>
                )}
                {hasValue(mappedProduct.activation) && (
                  <span className="inline-flex items-center gap-1 text-[10px] font-medium bg-white border border-blue-100 text-blue-700 px-2 py-1 rounded-full">
                    <FaCheckCircle className="w-2.5 h-2.5" />
                    Activation: {mappedProduct.activation}
                  </span>
                )}
                {hasValue(mappedProduct.delivery) && (
                  <span className="inline-flex items-center gap-1 text-[10px] font-medium bg-white border border-blue-100 text-blue-700 px-2 py-1 rounded-full">
                    <FaClock className="w-2.5 h-2.5" />
                    {mappedProduct.delivery}
                  </span>
                )}
              </div>
            )}

            {/* Lists */}
            <ReadMoreList
              items={mappedProduct.benefits}
              field="benefits"
              label="Benefits"
              showMore={showMore}
              toggleShowMore={toggleShowMore}
            />

            {isDigital && (
              <ReadMoreList
                items={mappedProduct.features}
                field="features"
                label="Key Features"
                showMore={showMore}
                toggleShowMore={toggleShowMore}
              />
            )}

            <ReadMoreList
              items={mappedProduct.instructionsForUse}
              field="instructionsForUse"
              label="How to Use"
              showMore={showMore}
              toggleShowMore={toggleShowMore}
            />

            {isPhysical && (
              <>
                <ReadMoreList
                  items={mappedProduct.packageContents}
                  field="packageContents"
                  label="Package Contents"
                  showMore={showMore}
                  toggleShowMore={toggleShowMore}
                />
                <ReadMoreList
                  items={mappedProduct.batteryInfo}
                  field="batteryInfo"
                  label="Battery Information"
                  showMore={showMore}
                  toggleShowMore={toggleShowMore}
                />
                <ReadMoreList
                  items={mappedProduct.operatingConditions}
                  field="operatingConditions"
                  label="Operating Conditions"
                  showMore={showMore}
                  toggleShowMore={toggleShowMore}
                />
                <ReadMoreList
                  items={mappedProduct.precautions}
                  field="precautions"
                  label="Precautions"
                  showMore={showMore}
                  toggleShowMore={toggleShowMore}
                />
                <ReadMoreList
                  items={mappedProduct.powerRequirements}
                  field="powerRequirements"
                  label="Power Requirements"
                  showMore={showMore}
                  toggleShowMore={toggleShowMore}
                />
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetails;
